import { useGetQuizTeacher } from '@repo/api/quiz/useGetQuizTeacher';
import { formatDuration, intervalToDuration } from 'date-fns';
import { CircleSlash } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';

import DeleteQuizMarkedAttemptDialog from '@/components/features/manage/manage-quizzes/DeleteQuizMarkedAttemptDialog';
import RecalculateMarkedAlertDialog from '@/components/features/manage/manage-quizzes/RecalculateMarkedAlertDialog';
import QuizManageHeader from '@/components/features/quiz/header/QuizManageHeader';
import QuizUsersAttemptsTable from '@/components/features/quiz/results/QuizUsersAttemptsTable';
import { PercentageHalfCircleChart } from '@/components/ui/chart';
import { EmptyState, EmptyStateCircles, EmptyStateContent, EmptyStateDescription, EmptyStateTitle } from '@/components/ui/empty-state';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useManageQuizStats } from '@/hooks/quiz';

export default function ManageQuizPage() {
  const { id } = useParams();
  const { data, isPending, isError, errorMessage } = useGetQuizTeacher(id);
  const [selectedAttemptId, setSelectedAttemptId] = useState<string | undefined>(undefined);
  const [openRecalculateDialog, setOpenRecalculateDialog] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const groupParam = searchParams.get('group');
  const selectedGroupId = groupParam ? Number(groupParam) : undefined;

  const filteredData = useMemo(() => {
    if (!data || selectedGroupId === undefined) return data;
    const group = data.groups.find((g) => g.id === selectedGroupId);
    const groupUserIds = new Set(group?.users.map((user) => user.id) ?? []);
    return {
      ...data,
      quizresps: data.quizresps.filter((attempt) => groupUserIds.has(Number(attempt.userId))),
    };
  }, [data, selectedGroupId]);

  const onGroupChange = (value: string) => {
    setSearchParams(
      (prev) => {
        if (value === 'all') {
          prev.delete('group');
        } else {
          prev.set('group', value);
        }
        return prev;
      },
      { replace: true }
    );
  };

  const stats = useManageQuizStats(data);

  if (isPending) {
    return (
      <div className="base-direction-col">
        <div className="flex w-full flex-col gap-2 rounded-2xl border p-4">
          <Skeleton className="h-5 w-12 rounded-sm" />
          <Skeleton className="my-2 h-4 w-24 rounded-sm" />
          <div className="flex flex-wrap gap-2">
            <Skeleton className="h-6 w-18 rounded-sm" />
            <Skeleton className="h-6 w-18 rounded-sm" />
            <Skeleton className="h-6 w-18 rounded-sm" />
            <Skeleton className="h-6 w-18 rounded-sm" />
            <Skeleton className="h-6 w-18 rounded-sm" />
          </div>
        </div>

        <div className="flex w-full grow flex-col gap-4 lg:flex-row">
          <div className="w-full min-w-64 lg:w-1/3 lg:max-w-60">
            <div className="h-full w-full overflow-hidden rounded-md border">
              <div className="border-b p-4">
                <Skeleton className="h-6 w-24" />
              </div>

              <div className="flex flex-row overflow-x-auto text-center text-xs lg:flex-col lg:divide-y">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="flex w-full min-w-60 flex-col items-center justify-between gap-4 p-4 lg:min-w-full">
                    <Skeleton className="h-20 w-20 rounded-full" />
                    <Skeleton className="h-4 w-28" />
                  </div>
                ))}

                <div className="hidden w-full min-w-60 flex-col items-center justify-center gap-1 p-4 lg:flex lg:min-w-full">
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-4 w-20" />
                </div>
              </div>

              <div className="flex w-full flex-col items-center justify-center gap-1 p-4 md:hidden">
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-4 w-20" />
              </div>
            </div>
          </div>

          <div className="flex-1">
            <div className="rounded-md border">
              <div className="border-b p-4">
                <div className="flex justify-between">
                  {['Uczeń', 'Punkty', 'Wynik', 'Czas rozpoczęcia', 'Czas podejścia'].map((_, i) => (
                    <Skeleton key={i} className="h-4 w-20" />
                  ))}
                </div>
              </div>
              <div className="space-y-3 p-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-4 w-16" />
                    <Skeleton className="h-4 w-12" />
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-4 w-16" />
                    <Skeleton className="h-8 w-16" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="base-direction-col">
        <EmptyState>
          <EmptyStateCircles className={'z-0'} />
          <FeaturedIcon size={'lg'} className={'z-10'}>
            <CircleSlash />
          </FeaturedIcon>
          <EmptyStateContent>
            <EmptyStateTitle>{errorMessage.title}</EmptyStateTitle>
            <EmptyStateDescription>{errorMessage.detail}</EmptyStateDescription>
          </EmptyStateContent>
        </EmptyState>
      </div>
    );
  }

  return (
    <div className="base-direction-col">
      {/* Controlled confirmation dialog */}
      <DeleteQuizMarkedAttemptDialog
        open={openDeleteDialog}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedAttemptId(undefined);
          }
          setOpenDeleteDialog(open);
        }}
        quizId={data.id}
        attemptId={selectedAttemptId}
      />
      <RecalculateMarkedAlertDialog
        id={data.id}
        attemptId={selectedAttemptId}
        open={openRecalculateDialog}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedAttemptId(undefined);
          }
          setOpenRecalculateDialog(open);
        }}
      />

      <QuizManageHeader quiz={data} />
      <div className="flex w-full grow flex-col gap-4 lg:flex-row">
        <div className="w-full min-w-64 lg:w-1/3 lg:max-w-60">
          <div className="bg-background h-full w-full overflow-hidden rounded-md border">
            <h5 className="border-b p-4 text-base font-semibold">Statystyki</h5>
            <div className="text-accent-foreground divide-border-primary flex flex-row overflow-x-auto text-center text-xs lg:flex-col lg:divide-y">
              <div className="flex w-full min-w-60 flex-col items-center justify-between p-4 lg:min-w-full">
                <PercentageHalfCircleChart
                  percentage={(stats.score.avg / data.points) * 100}
                  className="max-w-50"
                  labelClassName="[&_span]:text-[10px] md:[&_p]:text-lg lg:[&_p]:text-lg md:[&_span]:hidden lg:[&_span]:text-sm lg:[&_span]:block"
                />
                <span>
                  Średni wynik: {stats.score.avgFormatted} / {data.points}
                </span>
              </div>
              <div className="flex w-full min-w-60 flex-col items-center justify-between p-4 lg:min-w-full">
                <PercentageHalfCircleChart
                  percentage={(stats.attempt.attempt / stats.attempt.allowed) * 100}
                  className="max-w-50"
                  labelClassName="[&_span]:text-[10px] md:[&_p]:text-lg lg:[&_p]:text-lg md:[&_span]:hidden lg:[&_span]:text-sm lg:[&_span]:block"
                />
                <span>
                  Wypełniło: {stats.attempt.attempt} / {stats.attempt.allowed}
                </span>
              </div>
              <div className="flex w-full min-w-60 flex-col items-center justify-between p-4 lg:min-w-full">
                <PercentageHalfCircleChart
                  percentage={data.maxAttempts > 0 && stats.attempt.allowed > 0 ? (data.quizresps.length / stats.attempt.allowed / data.maxAttempts) * 100 : NaN}
                  className="max-w-50"
                  labelClassName="[&_span]:text-[10px] md:[&_p]:text-lg lg:[&_p]:text-lg md:[&_span]:hidden lg:[&_span]:text-sm lg:[&_span]:block"
                />
                <span>
                  Średnia ilość podejść: {stats.attempt.allowed > 0 ? (data.quizresps.length / stats.attempt.allowed).toPrecision(3) : '0'}
                  {data.maxAttempts > 0 && `/ ${data.maxAttempts}`}
                </span>
              </div>
              <div className="flex w-full min-w-60 flex-col items-center justify-center gap-1 p-4 lg:min-w-full">
                <p className="text-text-tertiary">Średni czas podejścia:</p>
                <p>
                  {data.quizresps.length > 0
                    ? formatDuration(
                        intervalToDuration({
                          start: 0,
                          end: (stats.attempt.time.sum / data.quizresps.length) * 1000,
                        })
                      )
                    : '0 min'}
                </p>
              </div>
            </div>
            <div className="flex w-full flex-col items-center justify-center gap-1 p-4 md:hidden">
              <p className="text-text-tertiary">Średni czas podejścia:</p>
              <p>
                {data.quizresps.length > 0
                  ? formatDuration(
                      intervalToDuration({
                        start: 0,
                        end: (stats.attempt.time.sum / data.quizresps.length) * 1000,
                      })
                    )
                  : '0 min'}
              </p>
            </div>
          </div>
        </div>
        <div className="flex w-full grow flex-col gap-4">
          {data.groups.length > 0 && (
            <Select value={selectedGroupId ? String(selectedGroupId) : 'all'} onValueChange={onGroupChange}>
              <SelectTrigger className="w-full max-w-xs">
                <SelectValue placeholder="Wszystkie grupy" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Wszystkie grupy</SelectItem>
                {data.groups.map((group) => (
                  <SelectItem key={group.id} value={String(group.id)}>
                    {group.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <QuizUsersAttemptsTable data={filteredData} setSelectedAttemptId={setSelectedAttemptId} setOpenDeleteDialog={setOpenDeleteDialog} setOpenRecalculateDialog={setOpenRecalculateDialog} />
        </div>
      </div>
    </div>
  );
}
